import React, { useState, useEffect, useRef } from 'react';

/**
 * Import notification rules from the practice's Excel workbook (commons #256,
 * first used for Vitality go-live #233).
 *
 * Flow: pick file → dry run (server parses + plans, writes nothing) → review
 * the rules to create and map any names the sheet uses that this practice
 * doesn't (providers, templates) → Import. Existing rules are never deleted;
 * optionally the currently active ones are set inactive first.
 *
 * Backend: POST /practices/:id/notifications/rules/import (multipart "file";
 * apply=1 to write). Same importer as scripts/notification-rules/import-xlsx.js.
 */
export default function NotificationRulesImportModal({ practiceId, providers, emailTemplates, smsTemplates, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('Import');
  const [deactivateExisting, setDeactivateExisting] = useState(true);
  const [skipUnknownTypes, setSkipUnknownTypes] = useState(false);
  const [defaultReminderEmail, setDefaultReminderEmail] = useState('');
  const [defaultReminderSms, setDefaultReminderSms] = useState('');
  const [providerAliases, setProviderAliases] = useState({});
  const [templateAliases, setTemplateAliases] = useState({});
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  // What the current plan was computed with. Mappings changed since → the plan
  // (and its blocking flag) is stale; Import re-plans before writing. GH-256:
  // the tester mapped every name, the stale plan still said "blocking", the
  // button was disabled with no visible reason.
  const planInputs = (pa, ta, de, ds, st) => JSON.stringify([pa, ta, de, ds, st]);
  const [plannedWith, setPlannedWith] = useState(null);
  const stale = !!plan && plannedWith !== planInputs(providerAliases, templateAliases, defaultReminderEmail, defaultReminderSms, skipUnknownTypes);
  const replanTimer = useRef(null);

  const providerName = (p) => (p.display_name || `${p.first_name || ''} ${p.last_name || ''}`).trim();

  const post = async (apply) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', label || 'Import');
    fd.append('apply', apply ? '1' : '0');
    fd.append('deactivateExisting', deactivateExisting ? '1' : '0');
    fd.append('skipUnknownTypes', skipUnknownTypes ? '1' : '0');
    if (defaultReminderEmail) fd.append('defaultReminderEmail', defaultReminderEmail);
    if (defaultReminderSms) fd.append('defaultReminderSms', defaultReminderSms);
    fd.append('providerAliases', JSON.stringify(providerAliases));
    fd.append('templateAliases', JSON.stringify(templateAliases));
    const token = localStorage.getItem('nh_auth_token') || localStorage.getItem('nh_context_token');
    const res = await fetch(`/api/practices/${practiceId}/notifications/rules/import`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      if (data.plan) setPlan(data.plan);
      throw new Error(data.error || `Import failed (${res.status})`);
    }
    return data;
  };

  const dryRun = async () => {
    if (!file) return null;
    setBusy(true); setError(''); setDone(null);
    let planned = null;
    try {
      const d = await post(false);
      planned = d.plan; setPlan(d.plan);
      setPlannedWith(planInputs(providerAliases, templateAliases, defaultReminderEmail, defaultReminderSms, skipUnknownTypes));
    } catch (e) { setError(e.message); }
    setBusy(false);
    return planned;
  };

  // Re-plan on its own once the user stops changing mappings.
  useEffect(() => {
    if (!plan || !stale || busy || done) return undefined;
    replanTimer.current = setTimeout(() => { dryRun(); }, 600);
    return () => clearTimeout(replanTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerAliases, templateAliases, defaultReminderEmail, defaultReminderSms, skipUnknownTypes]);

  const apply = async () => {
    setError('');
    // One click does it: re-plan with the current mappings if anything changed, then write.
    let current = plan;
    if (stale || plan?.blocking) {
      current = await dryRun();
      if (!current) return;
      if (current.blocking) {
        const u = current.unresolved || {};
        setError(`Still unresolved: ${[...(u.emailTemplates || []).map(n => `email "${n}"`), ...(u.smsTemplates || []).map(n => `SMS "${n}"`), ...(u.departments || []).map(n => `department "${n}"`)].join(', ')}`);
        return;
      }
    }
    setBusy(true);
    try {
      const d = await post(true);
      setDone(d.result);
      onImported && onImported(d.result);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const unresolved = plan?.unresolved || {};
  const blocking = plan?.blocking;

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.card} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Import rules from Excel</h3>
        <p style={S.help}>
          One row per Department × Provider × Appointment type, one column group per rule (Rule = Yes/No, Email Template, SMS Template).
          Nothing is written until you confirm. Existing rules are never deleted.
        </p>

        <div style={S.row}>
          <input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPlan(null); setDone(null); }} />
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Vitality)" style={{ ...S.input, width: 180 }} />
          <button style={S.btnSecondary} onClick={dryRun} disabled={!file || busy}>{busy && !plan ? 'Checking…' : 'Check file'}</button>
        </div>

        <div style={S.row}>
          <label style={S.chk}><input type="checkbox" checked={deactivateExisting} onChange={(e) => setDeactivateExisting(e.target.checked)} /> Set currently active rules to inactive first</label>
        </div>
        <div style={S.row}>
          <label style={S.chk}><input type="checkbox" checked={skipUnknownTypes} onChange={(e) => setSkipUnknownTypes(e.target.checked)} /> Skip rows whose appointment type isn't synced in this practice (otherwise the rule is still created)</label>
        </div>
        <div style={S.row}>
          <label style={S.lbl}>Default reminder templates (for "Followup office – reminders" Yes columns)</label>
          <select value={defaultReminderEmail} onChange={(e) => setDefaultReminderEmail(e.target.value)} style={S.input}>
            <option value="">Email: Appointment Reminder (default)</option>
            {emailTemplates.map(t => <option key={t.id} value={t.template_name}>Email: {t.template_name}</option>)}
          </select>
          <select value={defaultReminderSms} onChange={(e) => setDefaultReminderSms(e.target.value)} style={S.input}>
            <option value="">SMS: Appointment Reminder (default)</option>
            {smsTemplates.map(t => <option key={t.id} value={t.template_name}>SMS: {t.template_name}</option>)}
          </select>
        </div>

        {error && <div style={S.error}>{error}</div>}

        {plan && (
          <div style={{ marginTop: 12 }}>
            <div style={S.summary}>
              <span><strong>{plan.summary.sheetRows}</strong> sheet rows</span>
              <span><strong>{plan.summary.rowsWithRules}</strong> with rules</span>
              <span><strong>{plan.summary.rulesToCreate}</strong> rules to create</span>
              <span><strong>{plan.summary.actionsToCreate}</strong> actions</span>
            </div>
            {(plan.summary.warnings || []).map((w, i) => <div key={i} style={S.warn}>{w}</div>)}
            {plan.summary.skippedGroups?.length > 0 && <div style={S.note}>Skipped column groups (not rule-engine triggers): {plan.summary.skippedGroups.join('; ')}</div>}
            {plan.summary.unknownGroups?.length > 0 && <div style={S.warn}>Unrecognised column groups (ignored): {plan.summary.unknownGroups.join('; ')}</div>}

            {unresolved.providers?.length > 0 && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Providers in the sheet that don't match this practice — map, or leave as "skip" and their rows are not imported</div>
                {unresolved.providers.map(name => (
                  <div key={name} style={S.mapRow}>
                    <span style={S.mapName}>{name}</span>
                    <select value={providerAliases[name] || ''} onChange={(e) => setProviderAliases({ ...providerAliases, [name]: e.target.value })} style={S.input}>
                      <option value="">— skip —</option>
                      {providers.map(p => <option key={p.id} value={`${p.first_name || ''} ${p.last_name || ''}`.trim()}>{providerName(p)}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
            {(unresolved.emailTemplates?.length > 0 || unresolved.smsTemplates?.length > 0) && (
              <div style={S.section}>
                <div style={S.sectionTitle}>Template names in the sheet that don't exist here — map each to a template</div>
                {unresolved.emailTemplates?.map(name => (
                  <div key={`e-${name}`} style={S.mapRow}>
                    <span style={S.mapName}>Email: {name}</span>
                    <select value={templateAliases[name] || ''} onChange={(e) => setTemplateAliases({ ...templateAliases, [name]: e.target.value })} style={S.input}>
                      <option value="">— choose —</option>
                      {emailTemplates.map(t => <option key={t.id} value={t.template_name}>{t.template_name}</option>)}
                    </select>
                  </div>
                ))}
                {unresolved.smsTemplates?.map(name => (
                  <div key={`s-${name}`} style={S.mapRow}>
                    <span style={S.mapName}>SMS: {name}</span>
                    <select value={templateAliases[name] || ''} onChange={(e) => setTemplateAliases({ ...templateAliases, [name]: e.target.value })} style={S.input}>
                      <option value="">— choose —</option>
                      {smsTemplates.map(t => <option key={t.id} value={t.template_name}>{t.template_name}</option>)}
                    </select>
                  </div>
                ))}
                <div style={S.note}>{stale ? 'Re-checking with your mappings…' : 'The plan below reflects these mappings.'}</div>
              </div>
            )}
            {unresolved.departments?.length > 0 && <div style={S.error}>Departments not found in this practice: {unresolved.departments.join(', ')}</div>}
            {unresolved.appointmentTypes?.length > 0 && <div style={S.warn}>Appointment types not seen in this practice's synced list ({skipUnknownTypes ? `${plan.summary?.skippedTypeRows || 0} row(s) skipped` : 'rules still created'}): {unresolved.appointmentTypes.join(', ')}</div>}

            <div style={S.section}>
              <div style={S.sectionTitle}>Rules that will be created</div>
              <div style={{ maxHeight: 260, overflow: 'auto', fontSize: 12 }}>
                {plan.rules.map((r, i) => (
                  <div key={i} style={S.ruleRow}>
                    <div><strong>{r.rule_name}</strong></div>
                    <div style={{ color: '#374151' }}>{r.providers.map(p => p.name).join(', ')}</div>
                    <div style={{ color: '#6b7280' }}>{r.appointment_types.join(', ')}</div>
                    <div style={{ color: '#6b7280' }}>{r.actions.map(a => a.action_type).join(', ')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {done && <div style={S.ok}>Created {done.created.length} rules{done.deactivated.length ? `, set ${done.deactivated.length} existing rules inactive` : ''}.</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={S.btnSecondary} onClick={onClose} disabled={busy}>{done ? 'Close' : 'Cancel'}</button>
          {!done && (() => {
            const cannot = !plan ? 'Check the file first' : (blocking && !stale) ? 'Map the unresolved names above first' : (plan.rules.length === 0 && !stale) ? 'Nothing to import' : '';
            const off = busy || !!cannot;
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {cannot && plan && <span style={S.why}>{cannot}</span>}
                <button style={{ ...S.btnPrimary, ...(off ? S.btnOff : {}) }} onClick={apply} disabled={off} title={cannot}>
                  {busy && plan ? (stale ? 'Re-checking…' : 'Importing…') : `Import${plan && !stale ? ` ${plan.rules.length}` : ''} rules`}
                </button>
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  card: { background: '#fff', borderRadius: 8, padding: 24, width: '94%', maxWidth: 820, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  help: { fontSize: 12, color: '#6b7280', marginTop: 0 },
  row: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 },
  lbl: { fontSize: 12, color: '#374151', width: '100%' },
  input: { padding: '8px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', minWidth: 240 },
  chk: { fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  btnPrimary: { padding: '8px 14px', fontSize: 13, fontWeight: 500, color: '#fff', background: '#4f46e5', border: 'none', borderRadius: 6, cursor: 'pointer' },
  btnOff: { opacity: 0.45, cursor: 'not-allowed' },
  why: { fontSize: 12, color: '#b45309' },
  btnSecondary: { padding: '8px 14px', fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' },
  summary: { display: 'flex', gap: 18, fontSize: 13, padding: '8px 10px', background: '#f9fafb', borderRadius: 6 },
  section: { marginTop: 12 },
  sectionTitle: { fontSize: 13, fontWeight: 600, marginBottom: 6 },
  mapRow: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 },
  mapName: { fontSize: 13, minWidth: 300 },
  ruleRow: { padding: '6px 8px', borderBottom: '1px solid #f3f4f6' },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 13, marginTop: 8 },
  warn: { background: '#fffbeb', color: '#92400e', padding: 8, borderRadius: 6, fontSize: 12, marginTop: 8 },
  note: { fontSize: 12, color: '#6b7280', marginTop: 6 },
  ok: { background: '#ecfdf5', color: '#065f46', padding: 10, borderRadius: 6, fontSize: 13, marginTop: 12 },
};
